// Core Vue imports
// App services
import type { StateChange } from '@/ably'
import ElementVisibility from '@/components/ElementVisibility.vue'
import AppBanner from '@/components/banners/AppBanner.vue'
import AppAccordion from '@/components/common/AppAccordion.vue'
import AppAccordionSingle from '@/components/common/AppAccordionSingle.vue'
import AppCountdown from '@/components/common/AppCountdown.vue'
import AppFilter from '@/components/common/AppFilter.vue'
import AppTrustpilot from '@/components/common/AppTrustpilot.vue'
// Component imports (could be moved to separate file for better organization)
import SectionGuard from '@/components/common/SectionGuard.vue'
import AppDrawer from '@/components/drawers/AppDrawer.vue'
import AppIcon from '@/components/icons/AppIcon.vue'
import AppButton from '@/components/inputs/AppButton.vue'
import AppForm from '@/components/inputs/AppForm.vue'
import AppSelect from '@/components/inputs/AppSelect.vue'
import AppModal from '@/components/modals/AppModal.vue'
import AppSection from '@/components/sections/AppSection.vue'
import { i18n } from '@/i18n'
import { initializeGA } from '@/plugins/useGtm'
import { Calendar } from '@/services/calendar'
import { SettingsService } from '@/services/settings'
import { UUIDTracking } from '@/services/uuid-tracking'
// Stores
import { useAlertStore } from '@/stores/alert'
import { useFeatureFlagsStore } from '@/stores/feature-flags'
import { useNotificationStore } from '@/stores/notification'
//import { useUserStore } from '@/stores/user'
// Utility imports
import { Icons, InstantWinType } from '@/utils/helper-objects'
import {
  toCfAssetUrl,
  toCfResizeUrl,
  toFullUrl,
} from '@/utils/url-generation-utils'
import { pluralise } from '@/utils/utils'
// Third-party libraries
import Echo from '@ably/laravel-echo'
import { cookieBot } from '@ambitiondev/vue-cookiebot'
import InstantGames from '@core/vue-plugins/instant-games/plugin.ts'
import { type RumEvent, datadogRum } from '@datadog/browser-rum'
import { IconName, TabSheetType } from '@goodlifeplus/component-library/helpers'
import {
  IconButtonSize,
  IconButtonVariant,
} from '@goodlifeplus/component-library/helpers'
import {
  TextButtonIconPosition,
  TextButtonSize,
  TextButtonVariant,
} from '@goodlifeplus/component-library/helpers'
import { getTheme } from '@goodlifeplus/themes'
import type { SiteSlug } from '@goodlifeplus/themes/types'
import * as Ably from 'ably'
import 'animate.css'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import { DateTime } from 'luxon'
import { createPinia } from 'pinia'
import 'virtual:theme.css'
import { createApp, defineAsyncComponent } from 'vue'
import Toast, { useToast } from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import type { AppEnv } from '../env.d.ts'
import App from './App.vue'
import './assets/main.scss'
import ApiClient from './lib/ApiClient.ts'
import router from './router.ts'
import AuthenticationService from './services/AuthenticationService.ts'
import HostMessagingService from './services/HostMessagingService.ts'
import InstantGameEntryService from './services/InstantGameEntryService.ts'
import InstantGameService from './services/InstantGameService'
import InstantGameSessionService from './services/InstantGameSessionService'
import PrizeFulfilmentService from './services/PrizeFulfilmentService.ts'

//import { instantGames } from './games'

// Environment variables
const APP_ENV = import.meta.env.MODE as AppEnv
const APP_DEBUG = import.meta.env.GLP_APP_ENV !== 'production'

const hostMessagingService = new HostMessagingService(
  import.meta.env.GLP_HOST_APP_ORIGIN
)

const authenticationService = new AuthenticationService(
  import.meta.env.GLP_OPERATOR_API_URL,
  import.meta.env.GLP_OPERATOR_KEY_ID,
  import.meta.env.GLP_SITE_ID,
  hostMessagingService
)

const apiClient = new ApiClient(
  import.meta.env.GLP_OPERATOR_API_URL,
  import.meta.env.GLP_OPERATOR_KEY_ID,
  import.meta.env.GLP_SITE_ID,
  authenticationService
)

// Services
const prizeFulfilmentService = new PrizeFulfilmentService(apiClient)
const gameService = new InstantGameService(apiClient)
const gameSessionService = new InstantGameSessionService(apiClient)
const gameEntryService = new InstantGameEntryService(apiClient)

// Define global constants
export const globalConstants = {
  APP_ENV,
  APP_DEBUG,
  BASE_URL: import.meta.env.GLP_BASE_URL,
  PORTAL_URL: import.meta.env.GLP_PORTAL_URL,
  API_URL: import.meta.env.GLP_API_URL,
  GLX_URL: import.meta.env.GLP_GLX_URL,
  BROADCASTING_URL: import.meta.env.GLP_BROADCASTING_URL,
  GLIDE_URL: import.meta.env.GLP_GLIDE_URL,
  CMS_URL: import.meta.env.GLP_CMS_URL,
  CLOUDFLARE_ASSET_URL: import.meta.env.GLP_CLOUDFLARE_ASSET_URL,
  BIG_PRIZE_VALUE: Number(import.meta.env.GLP_BIG_PRIZE_VALUE ?? 9000),

  BUSINESS_NAME: import.meta.env.GLP_BUSINESS_NAME,
  BUSINESS_PHONE: import.meta.env.GLP_BUSINESS_PHONE,
  BUSINESS_EMAIL: import.meta.env.GLP_BUSINESS_EMAIL,
  BUSINESS_ADDRESS: import.meta.env.GLP_BUSINESS_ADDRESS,
  BUSINESS_REGISTERED_NAME: import.meta.env.GLP_BUSINESS_REGISTERED_NAME,
  BUSINESS_COMPLAINTS_EMAIL: import.meta.env.GLP_BUSINESS_COMPLAINTS_EMAIL,
  BUSINESS_WINNERS_EMAIL: import.meta.env.GLP_BUSINESS_WINNERS_EMAIL,
  BUSINESS_INSTANT_WINS_EMAIL: import.meta.env.GLP_BUSINESS_INSTANT_WINS_EMAIL,

  REFERRAL_CREDIT_VALUE: 20,
  REFERRAL_DISCOUNT_VALUE: 0.5,
  SOCIALS_ENTRIES_VALUE: 25,

  EARN_POINTS_SUBSCRIPTION_VALUE: 40,
  EARN_POINTS_YEAR_VALUE: 500,
  EARN_POINTS_SPEND_VALUE: 2,

  $p: pluralise,
  $router: router,
  $i18n: i18n.global,
  $toFullUrl: toFullUrl,
  $toCfResizeUrl: toCfResizeUrl,
  $toCfAssetUrl: toCfAssetUrl,
  $calendar: Calendar,
  $settings: SettingsService,
  $theme: getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug),
  $toast: useToast(),
  host: hostMessagingService,
  gameService,
  gameSessionService,
  authenticationService,
  prizeFulfilmentService,
  gameEntryService,
  DateTime,
  Icons,

  UI: {
    Icon: {
      Name: IconName,
    },
    IconButton: {
      Variant: IconButtonVariant,
      Size: IconButtonSize,
    },
    TextButton: {
      Variant: TextButtonVariant,
      Size: TextButtonSize,
      IconPosition: TextButtonIconPosition,
    },
    TabSheet: {
      Type: TabSheetType,
    },
  },
}

// Create pinia store
const pinia = createPinia()
pinia.use(() => ({
  ...globalConstants,
}))

// Create Vue application
const app = createApp(App)

// https://github.com/vuejs/core/issues/3031#issuecomment-1085291941
app.provide('globalConstants', globalConstants)

// Initialize UUID tracking (reads from Server-Timing or cookie)
UUIDTracking.initialize()

// Configure plugins
app.use(i18n)
app.use(pinia)
app.use(router)
app.use(FloatingVue, {
  themes: {
    tooltip: {
      triggers: ['hover', 'focus', 'touch', 'click'],
    },
  },
})
app.use(Toast, {
  maxToasts: 3,
  shareAppContext: true,
})
app.use(cookieBot, {
  cookieBotId: import.meta.env.GLP_COOKIEBOT_ID,
})

app.use(InstantGames, {
  gameService,
  gameSessionService,
  games: {
    [InstantWinType.InstantPack]: defineAsyncComponent(
      () => import('@/views/InstantWins/InstantPacksView.vue')
    ),
    [InstantWinType.MysteryBox]: defineAsyncComponent(
      () => import('@/views/InstantWins/InstantWinMysteryBoxView.vue')
    ),
    [InstantWinType.Standard]: defineAsyncComponent(
      () => import('@/views/InstantWins/InstantWinView.vue')
    ),
    [InstantWinType.InstantSpin]: defineAsyncComponent(
      () => import('@/views/InstantWins/InstantSpinsView.vue')
    ),
  },
})

initializeGA()
/*
app.use(
  createGtm({
    id: 'GTM-TVGHVDB4',
    // queryParams: {
    //   // Add URL query string when loading gtm.js with GTM ID (required when using custom environments)
    //   gtm_auth: 'AB7cDEf3GHIjkl-MnOP8qr',
    //   gtm_preview: 'env-4',
    //   gtm_cookies_win: 'x',
    // },
    // source: 'https://customurl.com/gtm.js', // Add your own serverside GTM script
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    nonce: '2726c7f26c', // Will add `nonce` to the script tag
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: true, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
)
*/
if (
  Number(import.meta.env.GLP_DATADOG_ENABLED) &&
  !window.IS_DATADOG_INITIALISED &&
  window.DD_RUM
) {
  window.IS_DATADOG_INITIALISED = true
  datadogRum.init({
    applicationId: import.meta.env.GLP_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.GLP_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'good-life-frontend',
    env: APP_ENV,
    sessionSampleRate: Number(import.meta.env.GLP_DATADOG_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: Number(
      import.meta.env.GLP_DATADOG_SESSION_REPLAY_SAMPLE_RATE
    ),
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: (event: RumEvent) => {
      const excludedResources =
        /goodlifeplus(.+)(\/src\/|\/data\/|\/node_modules\/|\/assets\/)/
      if (
        event.type === 'resource' &&
        excludedResources.test(event.resource.url)
      ) {
        return false
      }
      return true
    },
  })
}

// Register global components
// Consider using auto-registration or component libraries in the future
Object.entries({
  AppIcon,
  AppButton,
  AppCountdown,
  AppSection,
  AppFilter,
  AppModal,
  AppDrawer,
  AppAccordion,
  AppAccordionSingle,
  AppBanner,
  /*
  AppAccordion,

  AppAccordionSingle,
  AppBadge,
  AppBanner,
  AppButton,
  AppCarousel,
  AppCheckbox,
  AppCountdown,
  AppDrawer,
  AppFilter,
  */
  AppForm,
  /*
  AppGridCarousel,
  AppIcon,
  AppInformationModal,
  AppInput,
  AppList,
  AppMembershipButton,
  AppModal,
  AppPageTitle,
  AppPagination,
  AppPicture,
  AppProgress,
  AppRange,
  AppRegisterSignInModal,
  AppSection,
  */
  AppSelect,
  /*
  AppSocialIcons,
  AppSwitch,
  AppTextDisclosure,
  AppToggle,
  AppTrustpilot,
  AppUpgradeModal,
  AppYouTubeModal,
  */
  AppTrustpilot,
  SectionGuard,
  // Renderless components
  ElementVisibility,
}).forEach(([name, component]) => {
  app.component(name, component)
})

// Define global properties
app.config.globalProperties = {
  ...app.config.globalProperties,
  ...globalConstants,
  //$userStore: useUserStore(),
  //$plansStore: usePlansStore(),
  $alertStore: useAlertStore(),
  $features: useFeatureFlagsStore(),
  $notificationStore: useNotificationStore(),
}

app.config.performance = globalConstants.APP_DEBUG

// Configure Ably broadcasting
window.Ably = Ably
window.Echo = new Echo({
  broadcaster: 'ably',
  authEndpoint: import.meta.env.GLP_BROADCASTING_URL,
})

// Monitor Ably connection state
window.Echo.connector.ably.connection.on((stateChange: StateChange) => {
  if (stateChange.current === 'failed') {
    console.error('Ably connection failed:', stateChange.reason)
  }
  if (stateChange.current === 'connected') {
    console.log('Ably server connected')
  }
})

// Disable console in production
if (import.meta.env.GLP_APP_ENV === 'production') {
  console.log = () => {}
  console.debug = () => {}
  console.info = () => {}
  console.warn = () => {}
}

/**
 * Ensure device ID for guest users.
 *
 * @returns {string} Device ID
 */
function ensureDeviceId(): string {
  let deviceId = localStorage.getItem('device_id')
  if (!deviceId) {
    deviceId =
      'device_' +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    localStorage.setItem('device_id', deviceId)
  }

  return deviceId
}

/**
 * Initialize the application and mount it to the DOM
 */
async function initializeApp() {
  if (
    window.location.hostname === 'competitions.casino.org' ||
    window.location.hostname === 'goodlifeplus.com' ||
    window.location.hostname === 'goodlife.myonlinecasinos.co.uk' ||
    window.location.hostname === 'goodlife.lottoland.co.uk' ||
    window.location.hostname === 'saints.goodfanz.com' ||
    window.location.hostname === 'rangers.goodfanz.com' ||
    window.location.hostname === 'saintsprizes.com' ||
    window.location.hostname === 'rangersprizes.com' ||
    window.location.hostname === 'truenorthprizes.com'
  ) {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
  }

  ensureDeviceId()

  try {
    const featureFlagsStore = useFeatureFlagsStore()
    const cacheLoaded = featureFlagsStore.initializeFromCache()

    if (!cacheLoaded) {
      await featureFlagsStore.fetchFeatureFlags()
    }
  } catch (error) {
    console.error('Failed to initialize feature flags:', error)
  }

  app.mount('#app')

  hostMessagingService.connect()

  useToast().updateDefaults({
    container: document.getElementById('main-container')!,
  })
}
/*
RewardToastSystem.initialize({
  toast: useToast(),
  userStore: useUserStore(),
  i18n: globalConstants.$i18n,
})
*/
// Initialize when router is ready

void router.isReady().then(initializeApp)
